import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { updateData, trackQuoteAccepted } from '../../../services';
import { useState } from 'react';
import Modal from '../../helpers/Modal';
import { LeadTimeNotice } from '../../helpers/LeadTimeNotice';
const PAYMENT_TERMS_THRESHOLD = 25000;

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const getPaymentTerms = (totalAmount) => {
    const amount = Number(totalAmount);
    const isOverThreshold = !Number.isNaN(amount) && amount >= PAYMENT_TERMS_THRESHOLD;

    if (isOverThreshold) {
        return {
            heading: 'Orders over $25,000',
            intro: `The following payment schedule applies to this quote:`,
            terms: [
                '25% payment upon order confirmation',
                '50% payment due prior to commencement of manufacturing',
                '25% payment due prior to arrangement of shipping',
            ],
        };
    }

    return {
        heading: 'Orders under $25,000',
        intro: Number.isNaN(amount)
            ? 'The following payment terms apply to this quote:'
            : `The following payment terms apply to this quote:`,
        terms: [
            '100% payment due before manufacturing'
        ],
    };
};

export const AcceptQuoteForm = ({ quote, setQuotes }) => {
    const { token, base_url, setInvoices, currentLeadTime: leadTime, user, company } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmedBillingEmail, setConfirmedBillingEmail] = useState('');

    const trimmedBillingEmail = confirmedBillingEmail.trim();
    const isBillingEmailValid = Boolean(trimmedBillingEmail) && isValidEmail(trimmedBillingEmail);

    const openModal = () => {
        setConfirmedBillingEmail('');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setConfirmedBillingEmail('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSubmitting) return;

        if (!trimmedBillingEmail) {
            window.showAlert(400, 'Please enter a billing email address.');
            return;
        }

        if (!isValidEmail(trimmedBillingEmail)) {
            window.showAlert(400, 'Please enter a valid billing email address.');
            return;
        }
        
        setIsSubmitting(true);

        const data = {
            is_accepted: true,
            lead_time: leadTime,
            confirmed_billing_email: trimmedBillingEmail,
        };

        try {
            console.log('Accepting quote:', quote.id);
            const updated_quote = await updateData(token, `${base_url}/apis/billing/${quote.id}`, data);
            
            if (updated_quote.quote) {
                setQuotes(prevQuotes =>
                    prevQuotes.map(q => q.id === quote.id ? updated_quote.quote : q)
                );
                trackQuoteAccepted({
                    companyId: quote.company_id || company?.id || user?.company_id,
                    projectId: quote.project_site_id,
                    quote: updated_quote.quote,
                    value: updated_quote.quote.total_amount ?? quote.total_amount,
                });
                window.showAlert(200, 'Quote accepted successfully!');
                console.log('Quote accepted successfully!');
            }

            if (updated_quote.invoice) {
                setInvoices(prevInvoices => [...prevInvoices, updated_quote.invoice]);
            }

            setIsModalOpen(false);
            setConfirmedBillingEmail('');
        } catch (error) {
            console.error('Error accepting quote:', error);
            
            // Show error alert based on error type
            if (error.status === 404) {
                window.showAlert(404, 'Quote not found');
            } else if (error.status >= 400 && error.status < 500) {
                window.showAlert(error.status, 'Invalid quote data. Please check your input.');
            } else {
                window.showAlert(500, 'Failed to accept quote. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const paymentTerms = getPaymentTerms(quote?.total_amount);

    const acceptQuoteForm = (
        <div>
            <LeadTimeNotice />
            <h2>Accept Quote</h2>
            <p><strong>Quote: {quote?.quote_number}</strong></p>
            <p>{paymentTerms.intro}</p>
            <ul>
                {paymentTerms.terms.map((term) => (
                    <li key={term}>{term}</li>
                ))}
            </ul>

            <p>Accepting this quote will generate an invoice that you will receive via email.</p>

            <p>If you have questions about terms, contact us at <a href="mailto:ashley@mtsolar.us">ashley@mtsolar.us</a>.</p>

            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="confirmedBillingEmail" className="form-label">
                        Confirm billing email address to acknowledge and accept the invoice terms
                    </label>
                    <input
                        id="confirmedBillingEmail"
                        type="text"
                        name="confirmedBillingEmail"
                        value={confirmedBillingEmail}
                        onChange={(e) => setConfirmedBillingEmail(e.target.value)}
                        className="form-input"
                        placeholder="name@example.com"
                        autoComplete="off"
                        required
                        disabled={isSubmitting}
                    />
                </div>
            
            <div className="form-actions">
                <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-secondary"
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || !isBillingEmailValid}
                >
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin /> Accepting...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faCheck} /> Accept Quote
                        </>
                    )}
                </button>
            </div>
            </form>
        </div>
    );

    return (
        <>
            <button 
                onClick={openModal} 
                className="btn btn-action btn-primary"
                title="Accept this quote"
            >
                <FontAwesomeIcon icon={faCheck} /> Accept
            </button>
            {isModalOpen && (
                <Modal form={acceptQuoteForm} setIsModalOpen={closeModal} />
            )}
        </>
    );
};